import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import useOnEnhancedLearnMoreClick from 'dashboard/apps/InquiryTemplateEditor/hooks/useOnEnhancedLearnMoreClick';
import { ScreenFlowStepType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import LearnMoreTooltip from 'dashboard/apps/InquiryTemplateEditor/LearnMoreTooltip';
import { StepTypeItem } from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowAddStepButton/ScreenFlowAddStepButton';
import TemplateModuleCard from 'dashboard/apps/InquiryTemplateEditor/TemplateModuleCard';
import { Feature } from 'dashboard/models/inquiry-template-version';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';

export const CARD_WIDTH = 204;

const DropdownContainer = styled.div.attrs(() => ({
  role: 'dropdown',
}))`
  width: ${CARD_WIDTH}px;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: row;
  grid-gap: ${Spacing[2]};
  padding: ${Spacing[1]};
`;

const TooltipText = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  max-width: ${CARD_WIDTH * 1.5}px;
  white-space: pre-wrap;
`;

export interface ScreenFlowAddStepTooltipProps {
  stepTypeItems: StepTypeItem[];
  handleTooltipClicked: (stepType: ScreenFlowStepType) => void;
}

const ScreenFlowAddStepTooltip = ({
  stepTypeItems,
  handleTooltipClicked,
}: ScreenFlowAddStepTooltipProps) => {
  const handleEnhancedLearnMoreClick = useOnEnhancedLearnMoreClick();

  return (
    <DropdownContainer>
      {stepTypeItems.map(
        ({
          id,
          type,
          label,
          description,
          icon,
          rotationDegrees,
          tooltipLabel,
          disabled: stepTypeDisabled,
          isLimited,
          showLearnMoreTooltip,
        }) => {
          const templateModuleCard = (
            <TemplateModuleCard
              size='xs'
              justify='center'
              id={`screen-flow__add-step-card-${id}`}
              key={`screen-flow__add-step-card-${id}`}
              title={label}
              description={description}
              icon={icon}
              rotationDegrees={rotationDegrees}
              disabled={stepTypeDisabled}
              isLimited={isLimited}
              onClick={() => handleTooltipClicked(type)}
              withBorder
            />
          );
          if (tooltipLabel == null) {
            return templateModuleCard;
          } else if (showLearnMoreTooltip) {
            return (
              <LearnMoreTooltip
                side='right'
                id='add-step-tooltip__learn-more-tooltip'
                onClick={() => {
                  let feature = null;
                  switch (type) {
                    case ScreenFlowStepType.Screen:
                      feature = Feature.Step;
                      break;
                    case ScreenFlowStepType.Actions:
                      feature = Feature.Action;
                      break;
                    case ScreenFlowStepType.Branching:
                      feature = Feature.Conditional;
                      break;
                    case ScreenFlowStepType.Module:
                      feature = Feature.Module;
                      break;
                    default:
                      break;
                  }

                  handleEnhancedLearnMoreClick({
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    feature,
                  });
                }}
                tooltipContent={tooltipLabel}
                disabled={!stepTypeDisabled}
                key={`${type}-disabled-tooltip`}
                trigger={<span>{templateModuleCard}</span>}
                portal={false}
              />
            );
          } else {
            return (
              <Tooltip
                side='right'
                disabled={!stepTypeDisabled}
                key={`${type}-disabled-tooltip`}
                trigger={<span>{templateModuleCard}</span>}
                portal={false}
              >
                <TooltipText>{tooltipLabel}</TooltipText>
              </Tooltip>
            );
          }
        },
      )}
    </DropdownContainer>
  );
};

export default ScreenFlowAddStepTooltip;
